import {Component, EventEmitter, Input, OnChanges, Output} from "@angular/core";
import {createEvent} from "../misc/event.polyfill";
import {DealKeySelectionDialogComponent} from "./deal-key-selection-dialog.component";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {AssetReconciliationResultItem} from "./asset-reconciliation.model";
import {CDFAsset, CDFItem} from "../cdf/cdf.gql-model";
import {exposeMethodInDebugMode} from "../misc/expose-item-in-debug-mode.function";
import {BulkOrderItemSelectionDialogComponent} from "./bulk-order-item-selection-dialog.component";
import {logErrors} from "../misc/log-errors.decorator";
import {FilteredList} from "../helpers/filtered-list";

@Component({
  selector: "asset-reconciliation",
  templateUrl: "./asset-reconciliation.template.pug",
  providers: [DialogService],
})
export class AssetReconciliationComponent implements OnChanges {
  assetList: FilteredList<CDFAsset> = new FilteredList<CDFAsset>({
    searchKeys: [
      "serialNumber",
      "building",
      "floor",
      "productDescription",
      "floorMapId",
      "productNumber",
      "manufacturer",
      "deviceKey",
      "extraSearchString",
    ],
    booleanFilters: [{
      caption: "Only show assets that need a device key",
      check: (asset: CDFAsset, hideAssetsWithDeviceKey: boolean) => {
        if (!hideAssetsWithDeviceKey) return true;

        let allAssociated = asset.associated || asset.isUpdate;

        if (asset.accessories.length && asset.associated) {
          if (asset.accessories.some(it => !it.associated)) allAssociated = false;
        }

        return !allAssociated;
      },
      value: true,
    }],
  });

  ready: boolean = false;

  @Input()
  assets: CDFAsset[] = [];

  @Output("change")
  changeEmitter: EventEmitter<Event> = new EventEmitter<Event>();

  constructor(private dialogService: DialogService) {
    exposeMethodInDebugMode("qaAutoAssociateAll", () => this.qaAutoAssociateAll());
  }

  private qaAutoAssociateAll() {
    for (const asset of this.assets) {
      if (!asset.associated && asset.candidates.length) {
        asset.associateDevice(asset.candidates[0]);
      }

      for (const accessory of asset.accessories) {
        if (!accessory.bundled && !accessory.associated && accessory.candidates.length) {
          accessory.associateDevice(accessory.candidates[0]);
        }
      }
    }

    this.handle();
  }

  filterAssets() {
    if (!this.assets.length) return [];

    return this.assets.filter(asset => asset.dispositionInfo.isFutureFleet);
  }

  updateDetailAndDisplay() {
    this.assetList.items = this.filterAssets();

    this.assetList.refreshDisplay();
    this.handle();
  }

  ngOnChanges(): void {
    this.updateDetailAndDisplay();
  }

  handle() {
    const result = createEvent("change");

    const items: AssetReconciliationResultItem[] = [];
    let associatedAssetCount = 0;
    let associatedAccessoryCount = 0;
    let totalAssetCount = 0;
    let totalAccessoryCount = 0;

    for (const asset of this.assetList.items) {
      totalAssetCount += 1;
      if (asset.associated || asset.isUpdate) {
        associatedAssetCount += 1;
        items.push({rowId: asset.rowId, deviceId: asset.deviceId});
      }

      for (const accessory of asset.accessories) {
        if (!accessory.bundled) {
          totalAccessoryCount += 1;
          if ((asset.associated && accessory.associated) || (!asset.associated && asset.isUpdate)) {
            associatedAccessoryCount += 1;
            items.push({rowId: accessory.rowId, deviceId: accessory.deviceId});
          }
        }
      }
    }

    result["items"] = items;
    result["unconfiguredAssets"] = totalAssetCount - associatedAssetCount;
    result["unconfiguredAccessories"] = totalAccessoryCount - associatedAccessoryCount;

    this.ready = (totalAssetCount === associatedAssetCount) &&
      (totalAccessoryCount === associatedAccessoryCount) &&
      (totalAssetCount > 0);

    this.changeEmitter.emit(result);
  }

  @logErrors()
  async changeDeviceKey(asset: CDFItem) {
    const changed = await DealKeySelectionDialogComponent.show(this.dialogService, asset, this.assetList.items);

    if (changed) {
      this.assetList.refreshDisplay();
      this.handle();
    }
  }

  @logErrors()
  async changeBulkOrder(asset: CDFItem) {
    if (!asset.bulkOrderAvailable) return;

    const changed = await BulkOrderItemSelectionDialogComponent.show(this.dialogService, asset);

    if (changed) {
      this.assetList.refreshDisplay();
      this.handle();
    }
  }
}
